import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, useReducedMotion, useMotionValue, useTransform } from 'framer-motion';

import { useIsMobile } from 'Hooks/useIsMobile';

import './SwipeNavigator.css';

/**
 * Helper to check if current time is within a UTC window.
 * Same logic as BottomNavBar for consistency.
 */
const isWithinUtcWindow = (startUtc, endUtc) => {
  if (!startUtc || !endUtc) return false;
  const start = new Date(startUtc.endsWith('Z') ? startUtc : startUtc + 'Z');
  const end = new Date(endUtc.endsWith('Z') ? endUtc : endUtc + 'Z');
  const now = new Date();
  return now.getTime() >= start.getTime() && now.getTime() <= end.getTime();
};

/**
 * SwipeNavigator — Wraps content with horizontal swipe gesture navigation.
 *
 * Uses the same tab configuration as BottomNavBar to determine visible sections.
 * On swipe left → navigate to next section.
 * On swipe right → navigate to previous section.
 * Elastic bounce at edges (first/last section).
 * Disabled on desktop or when reduced motion is preferred.
 *
 * @param {{ children: React.ReactNode }} props
 */
const SwipeNavigator = ({ children }) => {
  const { isMobile } = useIsMobile();
  const prefersReducedMotion = useReducedMotion();
  const location = useLocation();
  const navigate = useNavigate();

  const currentUser = useSelector((state) => state.currentUser);
  const settings = useSelector((state) => state.settings);

  // Motion value for drag offset — used for opacity feedback
  const x = useMotionValue(0);
  const contentOpacity = useTransform(x, [-200, 0, 200], [0.5, 1, 0.5]);

  // Calculate visible sections — same logic as BottomNavBar
  const visibleSections = useMemo(() => {
    const isAssignmentsVisible = () => {
      const { acceptDeclineStartDate, acceptDeclineEndDate } = settings;
      if (!acceptDeclineStartDate || !acceptDeclineEndDate) return false;
      return isWithinUtcWindow(acceptDeclineStartDate, acceptDeclineEndDate);
    };

    const isInterestFormVisible = () => {
      const { applyStartDate, applyEndDate } = settings;
      if (!applyStartDate || !applyEndDate) return false;
      return isWithinUtcWindow(applyStartDate, applyEndDate);
    };

    const allTabs = [
      { path: '/mtp/profile', visible: true },
      { path: '/mtp/rep-work-application', visible: isInterestFormVisible() },
      { path: '/mtp/assignments', visible: isAssignmentsVisible() },
      { path: '/mtp/manage-rep', visible: currentUser?.isManager && settings?.commentsOpen === 'Yes' },
      { path: '/mtp/settings', visible: true },
    ];

    return allTabs.filter((tab) => tab.visible);
  }, [settings, currentUser]);

  // Find current section index
  const currentIndex = useMemo(() => {
    return visibleSections.findIndex(
      (section) => location.pathname === section.path || location.pathname.startsWith(section.path + '/')
    );
  }, [visibleSections, location.pathname]);

  const isFirstSection = currentIndex <= 0;
  const isLastSection = currentIndex >= visibleSections.length - 1;

  const handleDragEnd = useCallback(
    (_event, info) => {
      const { offset, velocity } = info;
      const swipeThreshold = 100;
      const velocityThreshold = 500;

      const swipedLeft = offset.x < -swipeThreshold || velocity.x < -velocityThreshold;
      const swipedRight = offset.x > swipeThreshold || velocity.x > velocityThreshold;

      if (swipedLeft && !isLastSection) {
        // Navigate to next section
        navigate(visibleSections[currentIndex + 1].path);
      } else if (swipedRight && !isFirstSection) {
        // Navigate to previous section
        navigate(visibleSections[currentIndex - 1].path);
      }
      // At edges → elastic bounce handled by dragElastic, no navigation
    },
    [currentIndex, isFirstSection, isLastSection, navigate, visibleSections]
  );

  // Desktop or reduced motion → render children without drag
  if (!isMobile || prefersReducedMotion) {
    return <>{children}</>;
  }

  return (
    <div className="swipe-navigator">
      <motion.div
        className="swipe-navigator__content"
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.2}
        onDragEnd={handleDragEnd}
        style={{ x, opacity: contentOpacity }}
      >
        {children}
      </motion.div>
    </div>
  );
};

SwipeNavigator.propTypes = {
  children: PropTypes.node.isRequired,
};

export default SwipeNavigator;
